import React from 'react';
import cx from 'classnames';
import Image from 'next/image';
import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';
import Row  from 'react-bootstrap/Row';
import Accordion  from '@/components/Accordion';
import StandardCard from '@/components/cards/StandardCard';
import FinePrint from '@/components/premium/Enrollment/Fineprint';
import SignUpButton from '@/components/premium/SignUpButton';
import IconComponent from '@/components/premium/utilities/IconComponent';
import { useTranslation } from '@/hooks/translation';
import useWindowSize from '@/hooks/useWindowSize';
import { csLoader } from '@/utilities/image';
import AskUsAnything from '@separate/components/AskUsAnything';
import BenefitsCarousel from '@separate/components/BenefitsCarousel';
import BenefitsCard from '@separate/components/BenefitsCarousel/BenefitsCard';
import SavingCard from '@separate/components/BenefitsCarousel/SavingCard';
import CouponAlert from '@separate/components/CouponAlert';
import PoderSaludCancellationPolicy from '@separate/components/PoderSaludCancellationPolicy';
import ReferAFriendTNC from '@separate/components/ReferAFriend/TermsAndConditions';
import { setAnalyticsUser } from '@separate/utilities/analytics';
import HeroComponent from './HeroComponent';
import styles from './premiumLanding.module.scss';

export default function Landing(props) {
  const { isUserLoading, isLoggedIn, user, couponText } = props;
  const isOnDesktop = useWindowSize();
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!user) return;

    setAnalyticsUser(user);
  }, [user]);

  const informationTextItems = (title, text) => {
    return `<span class=${styles.infoTitle}>${title}</span> ${text}`;
  };

  const isPremiumMember = isLoggedIn && user?.is_premium_member;

  const aboutItems = [ ...t("premium.landing.about.items", { returnObjects: true }) ];
  const informationItemsMobile = [ ...t("premium.landing.information.items_mobile", { returnObjects: true }), ];
  const informationItemsDesktop = [ ...t("premium.landing.information.items_desktop", { returnObjects: true }), ];
  const testimonialItems = [ ...t("premium.landing.testimonial.items", { returnObjects: true }), ];
  const faqItems = [ ...t("premium.landing.faq.items", { returnObjects: true }) ];

  return (
    <div className={styles.wrapper}>

      {couponText && <CouponAlert className={styles.desktopOnly} coupon={couponText} />}

      <HeroComponent couponText={couponText} />

      <Row className={cx(styles.about, "py-4")}>
        <p className={styles.title}>{t("premium.landing.about.title")}</p>
        {aboutItems?.map((item, index) => (
          <Col
            key={index}
            md={12}
            lg={4}
            className={cx(styles.aboutItems, "mb-3")}
          >
            <h2>{item.title}</h2>
            <p className={styles.aboutItemsOrder}>{`0${index + 1}`}</p>
            <p dangerouslySetInnerHTML={{ __html: item.text }} />
            <div className={styles.verticalLine} />
          </Col>
        ))}

        <SignUpButton
          loading={isUserLoading}
          isLoggedIn={isLoggedIn}
          isPremiumMember={isPremiumMember}
          className={cx(styles.button, "py-2")}
          couponText={couponText}
        />
      </Row>

      <div className={styles.askUsAnythingWrapper}>
        <AskUsAnything />
      </div>

      <Row className={cx(styles.testimonials, styles.benefitCard, 'justify-content-center px-2')}>
        <Col md={12} xs={12}>
          <div className={styles.testimonialsHeaderWrapper}>
            <div className={styles.line} />
            <h2
              dangerouslySetInnerHTML={{
                __html: t('premium.landing.benefits.title')
              }}
            />
          </div>
        </Col>

        <BenefitsCarousel
          loading={isUserLoading}
          isLoggedIn={isLoggedIn}
          isPremiumMember={isPremiumMember}
          couponText={couponText}
          offset={311}
        >
          <BenefitsCard />
        </BenefitsCarousel>
      </Row>

      <Row className={cx(styles.testimonials, "justify-content-center px-2")}>
        <Col md={12} xs={12} className="mb-4">
          <div className={styles.testimonialsHeaderWrapper}>
            <div className={styles.line} />
            <h2
              dangerouslySetInnerHTML={{
                __html: t("premium.landing.testimonial.title")
              }}
            />
          </div>
        </Col>
        {testimonialItems?.map((item, index) => (
          <Col
            key={index}
            md={8}
            lg={4}
            className={cx(styles.testimonialWrapper, "mb-4")}
          >
            <div className={cx(styles.avatarWrapper, "d-flex")}>
              <div className={cx(styles.avatar, "mb-3")}>
                <Image
                  src={item.image}
                  alt="avatar"
                  loader={csLoader}
                  layout="fill"
                />
              </div>

              <div className="d-flex flex-column justify-content-center mb-3 px-4">
                <strong className="d-block">{item.name}</strong>
                <span className="d-block">{item.location}</span>
                <div className={styles.horizontalLineDesktop} />
                <q dangerouslySetInnerHTML={{ __html: item.text }} />
              </div>
            </div>

            <q dangerouslySetInnerHTML={{ __html: item.text }} />

            {index != 2 && <div className={styles.horizontalLine} />}
          </Col>
        ))}
      </Row>

      <Row className={cx(styles.testimonials, styles.savings, "justify-content-center px-2")}>
        <Col>
          <div className={styles.testimonialsHeaderWrapper}>
            <div className={styles.line} />
            <h2
              dangerouslySetInnerHTML={{
                __html: t("premium.landing.savings.title")
              }}
            />
          </div>
          <Col className="d-flex justify-content-center">
            <p>{t("premium.landing.savings.description")}</p>
          </Col>
        </Col>

        <BenefitsCarousel>
          <SavingCard />
        </BenefitsCarousel>
      </Row>

      <Row className={cx(styles.information, "py-4")}>
        <Col md={12}>
          <div className={styles.informationHeaderWrapper}>
            <div className={styles.line} />
            <h2 className="mb-4">{t("premium.landing.information.title")}</h2>
          </div>
        </Col>

        {!isOnDesktop && informationItemsMobile?.map((item) => (
          <Col key={item.title} md={12} lg={4} className="mb-3">
            <p className={cx(styles.infoTitle, "mb-3")}>{item.title}</p>
            <p className={cx(styles.infoText, "mb-3")}>{item.text}</p>
          </Col>
        ))}

        <SignUpButton
          loading={isUserLoading}
          isLoggedIn={isLoggedIn}
          isPremiumMember={isPremiumMember}
          className={cx(styles.button, "py-2")}
          couponText={couponText}
        />

        {isOnDesktop && informationItemsDesktop?.map((item, index) => (
          <Col key={index} md={12} lg={4} className="mb-3">
            <StandardCard
              backgroundColor="#03C8B0"
              className={styles.card}
              bodyClassName={cx(styles.cardBody, "d-flex flex-column align-items-center")}>
              <IconComponent name={item.key} />
              <p className="mb-0" dangerouslySetInnerHTML={{ __html: informationTextItems(item.title, item.text)}} />
            </StandardCard>
          </Col>
        ))}
      </Row>

      <Row className={cx(styles.faqWrapper, "mt-5")}>
        <Col md={12} xs={12} className="mb-4">
          <div className={styles.faqHeaderWrapper}>
            <div className={styles.line} />
            <h2>{t("premium.landing.faq.title")}</h2>
          </div>
        </Col>

        <Col className="p-0">
          <Accordion
            items={faqItems}
            toggleClassName={styles.accordionToggle}
          />
        </Col>
      </Row>

      <Row>
        <Col lg={16} className={styles.disclaimerWrapper}>
          <p className={styles.disclaimer}>
            <em>{t("premium.landing.fineprint")}</em>
          </p>
        </Col>
        <FinePrint className={styles.finePrint} />
        {couponText && (
          <>
            <ReferAFriendTNC />
            <PoderSaludCancellationPolicy />
          </>
        )}
      </Row>
    </div>
  );
}

Landing.propTypes = {
  isUserLoading: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  user: PropTypes.object,
  couponText: PropTypes.any
};
